import { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

// ─── SLA thresholds (hours) per deal status ───────────────────────────
const SLA_HOURS_BY_STATUS: Record<string, number> = {
  pending: 24,
  review_in_progress: 48,
  nda_sent: 72,
  pending_client_info: 96,
};

// Statuses that require staff action today
const ACTION_PENDING_STATUSES = [
  'pending',
  'review_in_progress',
  'nda_sent',
  'pending_client_info',
  'loi_draft',
  'awaiting_signature',
];

export interface BriefingDeal {
  id: string;
  title: string;
  status: string;
  hoursOverdue?: number;
  updatedAt?: Date;
}

export interface BriefingNews {
  id: string;
  type: 'new_submission' | 'nda_signed' | 'unread_message';
  title: string;
  dealId?: string;
  createdAt: Date;
}

export interface BriefingMeeting {
  id: string;
  title: string;
  scheduledAt: Date;
  dealId?: string;
  jitsiUrl?: string;
}

export interface MorningBriefingData {
  urgent: BriefingDeal[];
  today: BriefingDeal[];
  news: BriefingNews[];
  meetings: BriefingMeeting[];
  loading: boolean;
  error: string | null;
}

function toDate(v: any): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Timestamp) return v.toDate();
  if (v?.toDate) return v.toDate();
  if (typeof v === 'string' || typeof v === 'number') return new Date(v);
  return undefined;
}

function hoursSince(d?: Date): number {
  if (!d) return 0;
  return (Date.now() - d.getTime()) / 36e5;
}

export function useMorningBriefing() {
  const { user } = useAuth();
  // ⚠️ Standard projet : identifiant utilisateur = user?.id (pas user?.uid)
  const staffId = user?.id;
  const staffEmail = user?.email ?? undefined;

  const [data, setData] = useState<MorningBriefingData>({
    urgent: [],
    today: [],
    news: [],
    meetings: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!staffId) {
      setData((d) => ({ ...d, loading: false }));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(now); endOfDay.setHours(23, 59, 59, 999);
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        // 1) Deals assignés au staff
        const dealsSnap = await getDocs(
          query(collection(db, 'deals'), where('assignedStaffId', '==', staffId)),
        );

        const urgent: BriefingDeal[] = [];
        const today: BriefingDeal[] = [];

        dealsSnap.forEach((doc) => {
          const d = doc.data() as any;
          const status: string = d.current_stage || d.currentStage || d.stage || d.status || 'pending';
          const updatedAt = toDate(d.updatedAt || d.updated_at || d.createdAt);
          const title = d.title || d.dealName || d.reference || 'Untitled Deal';

          const slaHours = SLA_HOURS_BY_STATUS[status];
          const overdue = slaHours ? hoursSince(updatedAt) - slaHours : 0;

          if (slaHours && overdue > 0) {
            urgent.push({ id: doc.id, title, status, hoursOverdue: Math.round(overdue), updatedAt });
          } else if (ACTION_PENDING_STATUSES.includes(status)) {
            today.push({ id: doc.id, title, status, updatedAt });
          }
        });

        urgent.sort((a, b) => (b.hoursOverdue ?? 0) - (a.hoursOverdue ?? 0));

        // 2) Nouveautés 24h — userNotifications
        const news: BriefingNews[] = [];
        try {
          const notifSnap = await getDocs(
            query(
              collection(db, 'userNotifications'),
              where('userId', '==', staffId),
              where('createdAt', '>=', Timestamp.fromDate(yesterday)),
              orderBy('createdAt', 'desc'),
            ),
          );
          notifSnap.forEach((doc) => {
            const n = doc.data() as any;
            const type = n.type as string;
            let mapped: BriefingNews['type'] | null = null;
            if (type === 'new_submission' || type === 'deal_submitted') mapped = 'new_submission';
            else if (type === 'nda_signed') mapped = 'nda_signed';
            else if ((type === 'message' || type === 'new_message') && n.seen === false) mapped = 'unread_message';
            if (!mapped) return;
            news.push({
              id: doc.id,
              type: mapped,
              title: n.title || n.message || '—',
              dealId: n.dealId,
              createdAt: toDate(n.createdAt) || new Date(),
            });
          });
        } catch (e) {
          console.warn('[MorningBriefing] userNotifications query failed:', e);
        }

        // 3) Réunions du jour
        const meetings: BriefingMeeting[] = [];
        try {
          const meetingsSnap = await getDocs(
            query(
              collection(db, 'meetings'),
              where('scheduledAt', '>=', Timestamp.fromDate(startOfDay)),
              where('scheduledAt', '<=', Timestamp.fromDate(endOfDay)),
              orderBy('scheduledAt', 'asc'),
            ),
          );
          meetingsSnap.forEach((doc) => {
            const m = doc.data() as any;
            const participants: string[] = Array.isArray(m.participants) ? m.participants : [];
            const isMine =
              m.createdBy === staffId ||
              m.staffId === staffId ||
              participants.includes(staffId) ||
              (staffEmail && participants.includes(staffEmail));
            if (!isMine) return;
            meetings.push({
              id: doc.id,
              title: m.title || m.subject || 'Meeting',
              scheduledAt: toDate(m.scheduledAt) || new Date(),
              dealId: m.dealId,
              jitsiUrl: m.jitsiUrl,
            });
          });
        } catch (e) {
          console.warn('[MorningBriefing] meetings query failed:', e);
        }

        if (!cancelled) {
          setData({ urgent, today, news, meetings, loading: false, error: null });
        }
      } catch (err: any) {
        if (!cancelled) {
          setData({
            urgent: [], today: [], news: [], meetings: [],
            loading: false,
            error: err?.message || 'Failed to load briefing',
          });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [staffId, staffEmail]);

  return data;
}
